/**
 * Resolve Names, as the portal does it: tab-portal src/lib/name-resolution.ts (qa c50f5268),
 * ported line for line. Keep the two in step.
 *
 * The one difference is the input. The portal reads sheet rows through the column mapping;
 * here the browser check (precheck.ts) has already turned the rows into the records the server
 * files, keyed by field name, so the "mapping" is each mapped field to itself. Everything
 * after that - which rows need resolving, the TIN hint, the classification, the 100% rows the
 * file already split, the AI tail, the summary and the CSV - is the portal's.
 *
 * Nothing here is stored or sent until the upload: the decisions ride the upload link
 * (nameDecisionsForUpload) and the server reads the file with them.
 */
import {
  AI_CONFIDENCE_THRESHOLD,
  classifyName,
  idTypeColumnForForm,
  legacyNameColumnsForForm,
  normalizeNameKey,
  resolveTinHint,
  rowHasMappedSplitNameData,
  splitNameDbColumnsForForm,
  splitPersonNameWithControl,
  splitNameMappingColumnsForForm,
  tinColumnForForm,
  type ClassifyNameResult,
  type NameClassification,
  type TinHint,
  type TinHintSource,
} from '@engine/intake/nameIdentification';
import type { Pub1220PayeeName } from '@engine/intake/pub1220';

export type NameResolutionRowKind = 'legacy' | 'preSplit';

export interface NameResolutionRow {
  /** Row index in the parsed sheet. */
  index: number;
  kind: NameResolutionRowKind;
  /** Grid label: legacy combined name or composed split name. */
  displayName: string;
  name1: string;
  name2: string;
  tinHint: TinHint;
}

export interface NameResolutionPlan {
  required: boolean;
  reason: string;
  legacyColumns: [string, string] | null;
  /** Distinct normalized names, in first-seen order. */
  distinctNames: string[];
  rows: NameResolutionRow[];
  tinHintByName: Record<string, TinHint>;
  tinHintSourceByName: Record<string, TinHintSource>;
}

/** A user-reviewable decision for one distinct name. */
export interface NameDecision extends ClassifyNameResult {
  name: string;
  /** Set once the reviewer overrides the machine answer. */
  overridden?: boolean;
  /** Row already had customer-mapped split fields: not sent to AI or re-classified. */
  customerSplitProvided?: boolean;
  /** What the tax ID in the file says, kept when the reviewer picks the other kind. */
  tinKind?: NameClassification;
}

/** The kind the tax ID in the file says, for the SSN/EIN badge: never the reviewer's choice. */
export function tinKindOf(decision: NameDecision): NameClassification | null {
  if (decision.tinKind) return decision.tinKind;
  return decision.tinLocked ? decision.classification : null;
}

export const PRE_SPLIT_DECISION_SIGNAL = 'Customer-provided split fields — already identified';

const emptyPlan = (reason: string): NameResolutionPlan => ({
  required: false,
  reason,
  legacyColumns: null,
  distinctNames: [],
  rows: [],
  tinHintByName: {},
  tinHintSourceByName: {},
});

/** The records are keyed by field name already, so each mapped field reads itself. */
export function identityColumns(mapping: Record<string, string> | undefined): Record<string, string> {
  const columns: Record<string, string> = {};
  for (const field of Object.values(mapping ?? {})) {
    if (field) columns[field] = field;
  }
  return columns;
}

const readField = (row: Record<string, unknown>, byField: Record<string, string>, field: string): string => {
  const column = byField[field];
  if (!column) return '';
  return String(row?.[column] ?? '').trim();
};

/** Display label for a row that already has mapped split columns populated. */
export function composeSplitDisplayName(
  row: Record<string, unknown>,
  byField: Record<string, string>,
  formType: string,
): string {
  const splitColumns = splitNameDbColumnsForForm(formType);
  const business1 = readField(row, byField, splitColumns[4]);
  const business2 = readField(row, byField, splitColumns[5]);
  if (business1) {
    return [business1, business2].filter(Boolean).join(' ').trim();
  }
  return [
    readField(row, byField, splitColumns[0]),
    readField(row, byField, splitColumns[1]),
    readField(row, byField, splitColumns[2]),
    readField(row, byField, splitColumns[3]),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

const normalizeDeclaredIdType = (raw: string | number | undefined | null): string => {
  const text = String(raw ?? '').trim();
  if (/^\d+(?:\.0+)?$/.test(text)) return String(parseInt(text, 10));
  return text;
};

/** 100% decision for rows the client already filed in split layout. */
export function buildPreSplitDecision(
  displayName: string,
  row: Record<string, unknown>,
  byField: Record<string, string>,
  formType: string,
): NameDecision {
  const idTypeField = idTypeColumnForForm(formType);
  const declared = idTypeField ? normalizeDeclaredIdType(readField(row, byField, idTypeField)) : '';

  let classification: NameClassification = 'individual';
  if (declared === '1') classification = 'business';
  else if (declared === '2') classification = 'individual';
  else {
    const splitColumns = splitNameDbColumnsForForm(formType);
    classification = readField(row, byField, splitColumns[4]) ? 'business' : 'individual';
  }

  return {
    name: displayName,
    classification,
    confidence: 1,
    conflict: false,
    tinLocked: false,
    signal: PRE_SPLIT_DECISION_SIGNAL,
    source: 'deterministic',
    customerSplitProvided: true,
  };
}

/** Merge legacy classifications with pre-split rows shown at 100%. */
export function buildFullDecisionSet(
  plan: NameResolutionPlan,
  legacyDecisions: NameDecision[],
  rows: Array<Record<string, unknown>>,
  byField: Record<string, string>,
  formType: string,
): NameDecision[] {
  const seenPreSplit = new Set<string>();
  const preSplitDecisions: NameDecision[] = [];

  for (const resolutionRow of plan.rows) {
    if (resolutionRow.kind !== 'preSplit') continue;
    const key = normalizeNameKey(resolutionRow.displayName);
    if (!key || seenPreSplit.has(key)) continue;
    seenPreSplit.add(key);
    preSplitDecisions.push(
      buildPreSplitDecision(resolutionRow.displayName, rows[resolutionRow.index] ?? {}, byField, formType),
    );
  }

  const legacyByKey = new Map(legacyDecisions.map((decision) => [normalizeNameKey(decision.name), decision]));
  const orderedLegacy = plan.distinctNames
    .map((name) => legacyByKey.get(normalizeNameKey(name)))
    .filter((decision): decision is NameDecision => decision != null);

  return [...preSplitDecisions, ...orderedLegacy];
}

/** Whether a decision still needs the server AI second pass. */
export function decisionNeedsAiPass(decision: NameDecision): boolean {
  return (
    !decision.overridden &&
    !decision.customerSplitProvided &&
    !decision.tinLocked &&
    decision.source !== 'ai' &&
    (decision.conflict || decision.confidence < AI_CONFIDENCE_THRESHOLD)
  );
}

export function buildNameResolutionPlan(
  formType: string,
  byField: Record<string, string>,
  rows: Array<Record<string, unknown>>,
): NameResolutionPlan {
  const legacyColumns = legacyNameColumnsForForm(formType);
  if (!legacyColumns) {
    return emptyPlan(`no legacy name columns defined for ${formType}`);
  }

  const [legacyField1, legacyField2] = legacyColumns;
  const legacyColumn1 = byField[legacyField1];
  const legacyColumn2 = byField[legacyField2];

  if (!legacyColumn1) {
    return emptyPlan(`${legacyField1} is not mapped — nothing to resolve`);
  }

  const mappedSplitColumns = splitNameMappingColumnsForForm(formType).filter((column) => byField[column]);

  const tinField = tinColumnForForm(formType);
  const tinColumn = tinField ? byField[tinField] : undefined;
  const idTypeField = idTypeColumnForForm(formType);
  const idTypeColumn = idTypeField ? byField[idTypeField] : undefined;

  const resolutionRows: NameResolutionRow[] = [];
  const tinHintByName: Record<string, TinHint> = {};
  const tinHintSourceByName: Record<string, TinHintSource> = {};
  const distinctNames: string[] = [];
  const seen = new Set<string>();

  rows.forEach((row, index) => {
    const name1 = String(row?.[legacyColumn1] ?? '').trim();
    const name2 = legacyColumn2 ? String(row?.[legacyColumn2] ?? '').trim() : '';
    const hasLegacy = Boolean(name1 || name2);
    const hasSplit = rowHasMappedSplitNameData(row, byField, formType);
    if (!hasLegacy && !hasSplit) return;

    // The TIN stays in the browser: only the derived hint is kept.
    const resolved = resolveTinHint(
      tinColumn ? String(row?.[tinColumn] ?? '') : '',
      idTypeColumn ? (row?.[idTypeColumn] as string | number | undefined) : undefined,
    );

    if (hasSplit) {
      const display = composeSplitDisplayName(row, byField, formType) || name1 || name2;
      if (!display) return;
      resolutionRows.push({ index, kind: 'preSplit', displayName: display, name1, name2, tinHint: resolved.hint });
      return;
    }

    const primary = name1 || name2;
    resolutionRows.push({ index, kind: 'legacy', displayName: primary, name1, name2, tinHint: resolved.hint });

    const key = normalizeNameKey(primary);
    if (!key) return;
    if (!seen.has(key)) {
      seen.add(key);
      distinctNames.push(key);
    }
    // First non-unknown hint wins for a given name.
    if (resolved.hint !== 'unknown' && !tinHintByName[key]) {
      tinHintByName[key] = resolved.hint;
      if (resolved.source) tinHintSourceByName[key] = resolved.source;
    }
  });

  const preSplitCount = resolutionRows.filter((row) => row.kind === 'preSplit').length;

  if (distinctNames.length === 0 && preSplitCount === 0) {
    return { ...emptyPlan('legacy name columns are mapped but every row has blank name values'), legacyColumns };
  }

  if (distinctNames.length === 0) {
    return {
      required: true,
      reason: 'customer-provided split fields only — already identified at 100%',
      legacyColumns,
      distinctNames: [],
      rows: resolutionRows,
      tinHintByName: {},
      tinHintSourceByName: {},
    };
  }

  const reason =
    mappedSplitColumns.length > 0 && preSplitCount > 0
      ? 'hybrid file — legacy names need review; pre-split rows at 100%'
      : mappedSplitColumns.length > 0
        ? `${legacyField1} has data while mapped split columns are empty on those rows`
        : `${legacyField1} mapped without split name columns`;

  return { required: true, reason, legacyColumns, distinctNames, rows: resolutionRows, tinHintByName, tinHintSourceByName };
}

/** Cap on names auto-sent to the AI second pass; the rest are the person's to send. */
export const AI_AUTO_PASS_LIMIT = 200;

/** Names per POST /v1/names/identify request. */
export const AI_CHUNK_SIZE = 500;

/** Rows rendered per page. */
export const RESOLVE_NAMES_PAGE_SIZE = 25;

/** Default confidence cutoff for the review list: the same threshold that routes names to AI. */
export const DEFAULT_REVIEW_THRESHOLD = 0.93;

/** Selectable cutoffs in the review filter. */
export const REVIEW_THRESHOLD_OPTIONS = [0.8, 0.9, 0.93, 0.95, 1] as const;

export type ConfidenceBand = 'high' | 'medium' | 'low';

export function confidenceBand(decision: NameDecision): ConfidenceBand {
  if (decision.conflict) return 'low';
  if (decision.confidence >= 0.95) return 'high';
  if (decision.confidence >= 0.8) return 'medium';
  return 'low';
}

export interface ResolutionSummary {
  total: number;
  individual: number;
  business: number;
  high: number;
  medium: number;
  low: number;
  conflicts: number;
  aiAssisted: number;
  overridden: number;
  /** Count below the review threshold (or conflicting). */
  belowThreshold: number;
}

export function summarizeDecisions(
  decisions: NameDecision[],
  threshold: number = DEFAULT_REVIEW_THRESHOLD,
): ResolutionSummary {
  const summary: ResolutionSummary = {
    total: decisions.length,
    individual: 0,
    business: 0,
    high: 0,
    medium: 0,
    low: 0,
    conflicts: 0,
    aiAssisted: 0,
    overridden: 0,
    belowThreshold: 0,
  };
  for (const decision of decisions) {
    if (decision.classification === 'business') summary.business += 1;
    else summary.individual += 1;
    summary[confidenceBand(decision)] += 1;
    if (decision.conflict) summary.conflicts += 1;
    if (decision.source === 'ai') summary.aiAssisted += 1;
    if (decision.overridden) summary.overridden += 1;
    if (decision.conflict || decision.confidence < threshold) summary.belowThreshold += 1;
  }
  return summary;
}

/**
 * Classify every distinct name locally. Only the uncertain tail is worth an API call.
 * Returns decisions in input order plus the names that still need AI.
 */
export function classifyLocally(
  distinctNames: string[],
  tinHintByName: Record<string, TinHint>,
  tinHintSourceByName: Record<string, TinHintSource> = {},
): { decisions: NameDecision[]; pending: string[] } {
  const decisions: NameDecision[] = [];
  const pending: string[] = [];
  for (const name of distinctNames) {
    const result = classifyName(name, { tinHint: tinHintByName[name], tinHintSource: tinHintSourceByName[name] });
    decisions.push({ ...result, name, source: 'deterministic' });
    // tinLocked names are decided by ID format alone; an AI opinion can't change them.
    if (!result.tinLocked && (result.conflict || result.confidence < AI_CONFIDENCE_THRESHOLD)) {
      pending.push(name);
    }
  }
  return { decisions, pending };
}

/** Merge AI results over the deterministic set, keeping the better answer. */
export function mergeAiResults(
  decisions: NameDecision[],
  aiResults: Array<ClassifyNameResult & { name: string }>,
): NameDecision[] {
  if (aiResults.length === 0) return decisions;
  const byName = new Map(aiResults.map((result) => [normalizeNameKey(result.name), result]));
  return decisions.map((decision) => {
    // A reviewer's own choice always wins over a later machine answer.
    if (decision.overridden) return decision;
    if (decision.customerSplitProvided) return decision;
    // A formatted SSN/EIN decided this one: AI never overrides ID format.
    if (decision.tinLocked) return decision;
    const ai = byName.get(normalizeNameKey(decision.name));
    if (!ai) return decision;
    if (decision.confidence >= ai.confidence && !decision.conflict) return decision;
    return { ...decision, ...ai, name: decision.name };
  });
}

/**
 * The reviewer's "No, it's a …": the other kind, final. When the file has an SSN or EIN, the
 * badge keeps showing it, and a choice against it stays marked as a TIN conflict - a warning,
 * not a block: the reviewer's answer is what is filed.
 */
export function toggleDecision(decisions: NameDecision[], name: string): NameDecision[] {
  return decisions.map((decision) => {
    if (decision.customerSplitProvided || decision.name !== name) return decision;
    const classification = decision.classification === 'business' ? ('individual' as const) : ('business' as const);
    const tinKind = tinKindOf(decision) ?? undefined;
    return {
      ...decision,
      classification,
      confidence: 1,
      conflict: tinKind !== undefined && tinKind !== classification,
      ...(tinKind ? { tinKind } : {}),
      overridden: true,
      source: 'user' as const,
      signal: 'confirmed by reviewer',
    };
  });
}

/**
 * What rides the upload link: reviewer overrides and AI answers the server cannot reproduce,
 * as the portal sends them with Load Data, plus every name the reviewer accepted from the
 * review list - accepting is their answer, so the file is not warned about those again.
 * Names the rules read confidently on their own, and pre-split rows, are left out.
 */
export function nameDecisionsForUpload(
  decisions: NameDecision[],
  threshold: number = DEFAULT_REVIEW_THRESHOLD,
): Array<{ key: string; classification: NameClassification; confidence: number; signal: string; source: 'ai' | 'user' }> {
  const out: Array<{
    key: string;
    classification: NameClassification;
    confidence: number;
    signal: string;
    source: 'ai' | 'user';
  }> = [];
  for (const decision of decisions) {
    if (decision.customerSplitProvided) continue;
    const key = normalizeNameKey(decision.name);
    if (!key) continue;
    const accepted = decision.conflict || decision.confidence < threshold;
    if (decision.overridden || accepted) {
      out.push({ key, classification: decision.classification, confidence: 1, signal: decision.signal, source: 'user' });
    } else if (decision.source === 'ai') {
      out.push({
        key,
        classification: decision.classification,
        confidence: decision.confidence,
        signal: decision.signal,
        source: 'ai',
      });
    }
  }
  return out;
}

const escapeCsvCell = (value: string): string => {
  let text = value ?? '';
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

/** The Resolve Names grid as CSV (all decisions, not just the filtered page). */
export function nameDecisionsCsv(decisions: NameDecision[], previewFor: (decision: NameDecision) => string): string {
  const headers = ['Name in file', 'Identified as', 'Confidence', 'Why', 'Will be filed as', 'Source', 'Overridden'];
  return [
    headers.join(','),
    ...decisions.map((decision) =>
      [
        escapeCsvCell(decision.name),
        escapeCsvCell(decision.classification),
        escapeCsvCell(`${Math.round(decision.confidence * 100)}%`),
        escapeCsvCell(decision.signal),
        escapeCsvCell(previewFor(decision)),
        escapeCsvCell(decision.source ?? 'rules'),
        escapeCsvCell(decision.overridden ? 'yes' : 'no'),
      ].join(','),
    ),
  ].join('\n');
}

/** Download the Resolve Names grid. */
export function exportNameDecisionsCsv(
  decisions: NameDecision[],
  previewFor: (decision: NameDecision) => string,
  filename = 'resolve-names.csv',
): void {
  const blob = new Blob([nameDecisionsCsv(decisions, previewFor)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** 1098-T student names are always filed as individuals: no business toggle. */
export function is1098TFormType(formType: string): boolean {
  return formType.replace(/[^A-Z0-9]/gi, '').toUpperCase() === '1098T';
}

/** Deterministic individual-only decisions for 1098-T (no AI second pass). */
export function classify1098TStudentNames(distinctNames: string[]): NameDecision[] {
  return distinctNames.map((name) => ({
    name,
    classification: 'individual',
    confidence: 1,
    conflict: false,
    tinLocked: false,
    signal: '1098-T student name',
    source: 'deterministic',
  }));
}

// ---------------------------------------------------------------------------
// IRS 1220 files. Not in the portal, which takes no 1220 file: e-file app only.
//
// A 1220 payee has one name line, surname first by the publication's convention, with a Type
// of TIN and a name control beside it. The engine reads it at parse time (intake/pub1220.ts);
// this shows that reading on the same Resolve Names step. The Type of TIN is the tax-ID hint,
// as RECIPIENT_ID_TYPE is in a CSV, so most lines settle at the portal's 97%. What is left for
// review: a name that disagrees with its Type of TIN, a line with no Type of TIN that the rules
// are unsure of, and a person whose name control matches no word in the line, where the order
// had to be assumed.
// ---------------------------------------------------------------------------

/** A Resolve Names plan for a 1220 file, plus each name's line and control for the person split. */
export interface Pub1220NamePlan extends NameResolutionPlan {
  payeeByName: Record<string, { line: string; nameControl: string }>;
}

export function buildPub1220NamePlan(payeeNames: Pub1220PayeeName[]): Pub1220NamePlan {
  const rows: NameResolutionRow[] = [];
  const distinctNames: string[] = [];
  const tinHintByName: Record<string, TinHint> = {};
  const tinHintSourceByName: Record<string, TinHintSource> = {};
  const payeeByName: Record<string, { line: string; nameControl: string }> = {};

  for (const payee of payeeNames) {
    const key = normalizeNameKey(payee.nameLine);
    if (!key) continue;
    // Only what kind of TIN was declared; the TIN itself was never read into this list.
    const resolved = resolveTinHint('', payee.typeOfTin);
    rows.push({
      index: payee.recordIndex,
      kind: 'legacy',
      displayName: payee.nameLine,
      name1: payee.nameLine,
      name2: payee.secondNameLine,
      tinHint: resolved.hint,
    });
    if (!(key in payeeByName)) {
      distinctNames.push(key);
      payeeByName[key] = { line: payee.nameLine, nameControl: payee.nameControl };
    }
    if (resolved.hint !== 'unknown' && !tinHintByName[key]) {
      tinHintByName[key] = resolved.hint;
      if (resolved.source) tinHintSourceByName[key] = resolved.source;
    }
  }

  return {
    required: distinctNames.length > 0,
    reason: distinctNames.length > 0 ? 'IRS 1220 payee name lines' : 'no payee names in the file',
    legacyColumns: null,
    distinctNames,
    rows,
    tinHintByName,
    tinHintSourceByName,
    payeeByName,
  };
}

/** Below the review cutoff, so a person read in an assumed order is looked at. */
export const PUB1220_ORDER_UNSURE_CONFIDENCE = 0.9;

/**
 * A person whose name control matches no word in the name line was split surname first because
 * the publication says so, not because the file showed it. Those go to review.
 */
export function checkPub1220NameOrder(
  decisions: NameDecision[],
  payeeByName: Pub1220NamePlan['payeeByName'],
): NameDecision[] {
  return decisions.map((decision) => {
    if (decision.classification !== 'individual' || decision.overridden || decision.source === 'user') return decision;
    const payee = payeeByName[decision.name];
    if (!payee || !payee.line.trim().includes(' ')) return decision;
    const control = payee.nameControl;
    if (splitPersonNameWithControl(payee.line, control).method !== 'rules') return decision;
    if (decision.confidence <= PUB1220_ORDER_UNSURE_CONFIDENCE) return decision;
    return {
      ...decision,
      confidence: PUB1220_ORDER_UNSURE_CONFIDENCE,
      signal: control
        ? `${decision.signal}; name control ${control} matches no word in the name, so it was read surname first`
        : `${decision.signal}; no name control in the file, so it was read surname first`,
    };
  });
}

/** How a 1220 payee will be filed, the way the engine splits it (intake/pub1220.ts). */
export function previewPub1220Name(
  decision: NameDecision,
  row: { name1: string; name2: string } | undefined,
  nameControl: string,
  is1098T: boolean,
): string {
  const line = row?.name1 ?? decision.name;
  if (decision.classification === 'business' && !is1098T) {
    return [line, row?.name2 ?? ''].filter(Boolean).join(' / ');
  }
  const split = splitPersonNameWithControl(line, nameControl);
  return [split.first || line, split.middle, split.last, split.suffix].filter(Boolean).join(' · ');
}
